import React, { useMemo, useState, useEffect } from 'react';
import { Quest } from '../../types';
import { useUser } from '../../contexts/UserContext';
import { useLanguage } from '../../contexts/LanguageContext';

interface HistoryPageProps {
  quests: Quest[];
}

const HistoryPage: React.FC<HistoryPageProps> = ({ quests }) => {
  const { profile, deleteQuest, updateQuest, showNotification } = useUser();
  const { t } = useLanguage();
  const [currentPage, setCurrentPage] = useState(1);
  const [showClearModal, setShowClearModal] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const itemsPerPage = 15;
  
  const completedQuests = quests.filter(q => q.status === 'completed').sort((a, b) => {
    const timeA = a.completedAt || 0;
    const timeB = b.completedAt || 0;
    return timeB - timeA;
  });

  // Calculate pagination
  const totalPages = Math.ceil(completedQuests.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedQuests = completedQuests.slice(startIndex, endIndex);

  // Use lifetime stats from profile (persists even after clearing history)
  const stats = useMemo(() => {
    if (!profile?.lifetimeStats) {
      return {
        totalQuestsCompleted: 0,
        totalXPEarned: 0,
        totalCurrencyEarned: 0,
        byDifficulty: { easy: 0, normal: 0, hard: 0, boss: 0 },
        byType: { daily: 0, main: 0, instant: 0, boss: 0 },
      };
    }
    
    return {
      totalQuestsCompleted: profile.lifetimeStats.totalQuestsCompleted,
      totalXPEarned: profile.lifetimeStats.totalXPEarned,
      totalCurrencyEarned: profile.lifetimeStats.totalCurrencyEarned,
      byDifficulty: profile.lifetimeStats.questsByDifficulty,
      byType: profile.lifetimeStats.questsByType,
    };
  }, [profile]);

  const handleClearCompleted = async () => {
    setIsClearing(true);
    try {
      // Calculate tomorrow's date for resetting daily quests
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, '0')}-${String(tomorrow.getDate()).padStart(2, '0')}`;

      // Separate daily quests from regular quests
      const dailyQuests = completedQuests.filter(q => q.resetDaily);
      const regularQuests = completedQuests.filter(q => !q.resetDaily);

      // Reset daily quests to active status for tomorrow (preserve their schedule)
      const dailyResetPromises = dailyQuests.map(quest =>
        updateQuest(quest.id, {
          status: 'active',
          completedAt: null,
          scheduledFor: tomorrowStr // Keep them scheduled for tomorrow
        }, true) // silent = true
      );

      // Delete regular completed quests
      const regularDeletePromises = regularQuests.map(quest =>
        deleteQuest(quest.id, true) // silent = true
      );

      // Execute all operations in parallel
      await Promise.all([...dailyResetPromises, ...regularDeletePromises]);

      const totalProcessed = dailyQuests.length + regularQuests.length;
      setShowClearModal(false);
      setCurrentPage(1);

      // Show appropriate success message
      if (dailyQuests.length > 0 && regularQuests.length > 0) {
        showNotification(`✅ Cleared ${regularQuests.length} quest${regularQuests.length > 1 ? 's' : ''} from history & reset ${dailyQuests.length} daily quest${dailyQuests.length > 1 ? 's' : ''} for tomorrow!`, 'success');
      } else if (dailyQuests.length > 0) {
        showNotification(`✅ Reset ${dailyQuests.length} daily quest${dailyQuests.length > 1 ? 's' : ''} for tomorrow!`, 'success');
      } else {
        showNotification(`✅ Cleared ${regularQuests.length} quest${regularQuests.length > 1 ? 's' : ''} from history!`, 'success');
      }
    } catch (error) {
      console.error('Error clearing completed quests:', error);
      showNotification('❌ Failed to clear quest history', 'warning');
    } finally {
      setIsClearing(false);
    }
  };

  const difficultyColors: Record<string, string> = {
    easy: 'text-green-400',
    normal: 'text-blue-400',
    hard: 'text-orange-400',
    boss: 'text-red-400',
  };

  const typeIcons: Record<string, string> = {
    daily: '/icons/icon-quest-daily.png',
    main: '/icons/icon-quest-main.png',
    instant: '/icons/icon-quest-instant.png',
    boss: '/icons/icon-quest-boss.png',
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  return (
    <div className="animate-fade-in max-w-7xl mx-auto px-3 sm:px-6 py-3 sm:py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 sm:mb-6 flex-wrap gap-3 sm:gap-4">
        <div>
          <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
            <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-system-blue rounded-full animate-pulse"></div>
            <span className="font-orbitron text-lg sm:text-2xl tracking-wider text-system-blue uppercase">
              ［ {t.history.title} ］
            </span>
          </div>
          <p className="text-xs sm:text-sm text-gray-400 font-rajdhani">
            {t.history.subtitle}
          </p>
        </div>
        
        {/* Clear Completed Button */}
        {completedQuests.length > 0 && (
          <button
            onClick={() => setShowClearModal(true)}
            className="relative overflow-hidden bg-gradient-to-b from-red-700/60 via-red-800/60 to-red-900/60 hover:from-red-600/70 hover:via-red-700/70 hover:to-red-800/70 text-red-400 px-3 sm:px-4 py-1.5 sm:py-2 rounded-2xl font-rajdhani font-bold text-xs sm:text-base transition-all flex items-center gap-1.5 sm:gap-2 border-t-2 border-l-2 border-red-500/50 border-r-2 border-b-2 border-r-red-800/70 border-b-red-900/70 hover:scale-105 active:scale-95"
            style={{
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4), 0 1px 4px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 100, 100, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
            }}
          >
            <span>🗑️</span>
            <span>CLEAR HISTORY</span>
          </button>
        )}
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-6">
        <div 
          className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-2 sm:p-4 hover:border-t-blue-400/70 hover:border-l-blue-400/70 hover:border-r-blue-600/80 hover:border-b-blue-700/80 transition-all"
          style={{
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
          }}
        >
          <div className="text-[10px] sm:text-xs text-gray-400 font-rajdhani uppercase mb-1 sm:mb-2">{t.stats.completed}</div>
          <div className="text-2xl sm:text-3xl font-bold font-orbitron text-system-blue">{stats.totalQuestsCompleted}</div>
          <div className="text-[9px] sm:text-[10px] text-gray-500 mt-0.5 sm:mt-1">History: {completedQuests.length}</div>
        </div>
        <div 
          className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-2 sm:p-4 hover:border-t-purple-400/70 hover:border-l-purple-400/70 hover:border-r-purple-600/80 hover:border-b-purple-700/80 transition-all"
          style={{
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
          }}
        >
          <div className="text-[10px] sm:text-xs text-gray-400 font-rajdhani uppercase mb-1 sm:mb-2">{t.stats.totalXP}</div>
          <div className="text-2xl sm:text-3xl font-bold font-orbitron text-purple-400">{stats.totalXPEarned}</div>
        </div>
        <div 
          className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-2 sm:p-4 hover:border-t-yellow-400/70 hover:border-l-yellow-400/70 hover:border-r-yellow-600/80 hover:border-b-yellow-700/80 transition-all"
          style={{
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
          }}
        >
          <div className="text-[10px] sm:text-xs text-gray-400 font-rajdhani uppercase mb-1 sm:mb-2">{t.history.currentCurrency}</div>
          <div className="text-2xl sm:text-3xl font-bold font-orbitron" style={{ color: '#FCD34D' }}>{profile?.currency || 0}</div>
          <div className="text-[9px] sm:text-[10px] text-gray-500 mt-0.5 sm:mt-1">{t.history.earned}: {stats.totalCurrencyEarned}</div>
        </div>
        <div 
          className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-2 sm:p-4 hover:border-t-red-400/70 hover:border-l-red-400/70 hover:border-r-red-600/80 hover:border-b-red-700/80 transition-all"
          style={{
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
          }}
        >
          <div className="text-[10px] sm:text-xs text-gray-400 font-rajdhani uppercase mb-1 sm:mb-2">{t.history.bossDefeated}</div>
          <div className="text-2xl sm:text-3xl font-bold font-orbitron text-red-400">{stats.byDifficulty.boss}</div>
        </div>
      </div>

      {/* Breakdown Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
        <div 
          className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-3 sm:p-4 transition-all"
          style={{
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
          }}
        >
          <h3 className="text-xs sm:text-sm font-bold font-orbitron text-system-blue mb-2 sm:mb-3">{t.history.byDifficulty}</h3>
          <div className="space-y-1.5 sm:space-y-2">
            {Object.entries(stats.byDifficulty).map(([difficulty, count]) => (
              <div key={difficulty} className="flex items-center justify-between">
                <span className={`font-rajdhani text-xs sm:text-sm uppercase ${difficultyColors[difficulty]}`}>
                  {t.difficulty[difficulty as keyof typeof t.difficulty]}
                </span>
                <span className="font-bold font-orbitron text-sm sm:text-base text-white">{count}</span>
              </div>
            ))}
          </div>
        </div>
        <div 
          className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-3 sm:p-4 transition-all"
          style={{
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
          }}
        >
          <h3 className="text-xs sm:text-sm font-bold font-orbitron text-system-blue mb-2 sm:mb-3">{t.history.byType}</h3>
          <div className="space-y-1.5 sm:space-y-2">
            {Object.entries(stats.byType).map(([type, count]) => (
              <div key={type} className="flex items-center justify-between">
                <span className="font-rajdhani text-xs sm:text-sm uppercase text-gray-300 flex items-center gap-1.5 sm:gap-2">
                  <img 
                    src={typeIcons[type]} 
                    alt={type}
                    className="w-4 h-4 sm:w-5 sm:h-5 object-contain"
                    style={{ filter: 'drop-shadow(0 0 4px rgba(59, 130, 246, 0.6))' }}
                  />
                  <span>{t.quests[type as 'daily' | 'main' | 'instant' | 'boss']}</span>
                </span>
                <span className="font-bold font-orbitron text-sm sm:text-base text-white">{count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quest List */}
      <div className="space-y-2 sm:space-y-3">
        <div className="flex items-center justify-between mb-2 sm:mb-3">
          <h3 className="text-xs sm:text-sm font-bold font-orbitron text-system-blue">{t.history.completedQuests.toUpperCase()}</h3>
          {completedQuests.length > 0 && (
            <span className="text-[10px] sm:text-xs text-gray-500 font-rajdhani">
              Showing {startIndex + 1}-{Math.min(endIndex, completedQuests.length)} of {completedQuests.length}
            </span>
          )}
        </div>
        {completedQuests.length === 0 ? (
          <div className="text-center py-8 sm:py-12 text-gray-400">
            <div className="text-4xl sm:text-6xl mb-3 sm:mb-4 opacity-50">📭</div>
            <p className="font-rajdhani text-base sm:text-lg">{t.history.noHistory}</p>
            <p className="font-rajdhani text-xs sm:text-sm mt-1 sm:mt-2">{t.dashboard.noQuestsDesc}</p>
          </div>
        ) : (
          paginatedQuests.map((quest, index) => (
            <div
              key={quest.id}
              className="bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl p-3 sm:p-4 hover:border-t-blue-400/70 hover:border-l-blue-400/70 hover:border-r-blue-600/80 hover:border-b-blue-700/80 transition-all animate-fade-in"
              style={{ 
                animationDelay: `${index * 0.03}s`,
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
              }}
            >
              <div className="flex items-start justify-between gap-3 sm:gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 sm:gap-2 mb-1 sm:mb-2">
                    <img 
                      src={typeIcons[quest.type]} 
                      alt={quest.type}
                      className="w-5 h-5 sm:w-6 sm:h-6 object-contain flex-shrink-0"
                      style={{ filter: 'drop-shadow(0 0 4px rgba(59, 130, 246, 0.6))' }}
                    />
                    <h4 className="font-orbitron font-bold text-sm sm:text-base text-white truncate">{quest.title}</h4>
                  </div>
                  {quest.description && (
                    <p className="text-xs sm:text-sm text-gray-400 font-rajdhani mb-1 sm:mb-2 line-clamp-2">{quest.description}</p>
                  )}
                  <div className="flex items-center gap-2 sm:gap-3 text-[10px] sm:text-xs flex-wrap">
                    <span className={`font-semibold font-rajdhani uppercase ${difficultyColors[quest.difficulty]}`}>
                      {t.difficulty[quest.difficulty as keyof typeof t.difficulty]}
                    </span>
                    <span className="text-gray-500">•</span>
                    <span className="text-gray-400 font-rajdhani truncate">
                      {quest.completedAt && formatDate(quest.completedAt)}
                    </span>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-[10px] sm:text-xs text-gray-400 font-rajdhani mt-0.5 sm:mt-1 flex flex-col sm:flex-row items-end sm:items-center gap-1 sm:gap-2">
                    <span className="text-system-blue font-bold whitespace-nowrap">+{quest.xpReward} XP</span>
                    <span className="hidden sm:inline">•</span>
                    <span className="flex items-center gap-0.5 sm:gap-1 whitespace-nowrap">
                      <span className="text-xs sm:text-sm">💰</span>
                      <span className="font-bold" style={{ color: '#FCD34D' }}>+{quest.currencyReward}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1 sm:gap-2 mt-4 sm:mt-6">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-2xl font-rajdhani font-bold text-xs sm:text-base transition-all ${
              currentPage === 1
                ? 'bg-gradient-to-b from-slate-800/60 via-slate-900/60 to-slate-950/60 text-gray-600 cursor-not-allowed border-t-2 border-l-2 border-slate-900/70 border-r-2 border-b-2 border-r-slate-950/80 border-b-slate-950/80'
                : 'bg-gradient-to-b from-slate-700/60 via-slate-800/60 to-slate-900/60 hover:from-slate-600/70 hover:via-slate-700/70 hover:to-slate-800/70 text-system-blue border-t-2 border-l-2 border-slate-500/50 border-r-2 border-b-2 border-r-slate-800/70 border-b-slate-900/70 hover:scale-105'
            }`}
            style={currentPage !== 1 ? {
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4), 0 1px 4px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
            } : {
              boxShadow: 'inset 0 2px 6px rgba(0, 0, 0, 0.6)'
            }}
          >
            <span className="hidden sm:inline">← Previous</span>
            <span className="sm:hidden">←</span>
          </button>

          <div className="flex items-center gap-0.5 sm:gap-1">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
              // Show first page, last page, current page, and pages around current
              const showPage =
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 1 && page <= currentPage + 1);
              
              const showEllipsis =
                (page === currentPage - 2 && currentPage > 3) ||
                (page === currentPage + 2 && currentPage < totalPages - 2);

              if (!showPage && !showEllipsis) return null;

              if (showEllipsis) {
                return (
                  <span key={page} className="px-1 sm:px-2 text-xs sm:text-base text-gray-600">
                    ...
                  </span>
                );
              }

              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`w-8 h-8 sm:w-10 sm:h-10 rounded-2xl font-rajdhani font-bold text-xs sm:text-base transition-all relative overflow-hidden ${
                    currentPage === page
                      ? 'bg-system-blue text-black hover:scale-105 active:scale-95'
                      : 'bg-gradient-to-b from-slate-700/60 via-slate-800/60 to-slate-900/60 hover:from-slate-600/70 hover:via-slate-700/70 hover:to-slate-800/70 text-system-blue border-t-2 border-l-2 border-slate-500/50 border-r-2 border-b-2 border-r-slate-800/70 border-b-slate-900/70 hover:scale-105'
                  }`}
                  style={currentPage === page ? {
                    borderTop: '2px solid rgba(255, 255, 255, 0.3)',
                    borderLeft: '2px solid rgba(255, 255, 255, 0.3)',
                    borderRight: '2px solid rgba(0, 0, 0, 0.4)',
                    borderBottom: '2px solid rgba(0, 0, 0, 0.4)',
                    boxShadow: '0 6px 16px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.2), inset 0 2px 4px rgba(255, 255, 255, 0.2), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                  } : {
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4), 0 1px 4px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                  }}
                >
                  {currentPage === page && (
                    <div className="absolute inset-0 bg-gradient-to-b from-white/20 via-transparent to-black/30 pointer-events-none" />
                  )}
                  <span className="relative">{page}</span>
                </button>
              );
            })}
          </div>

          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-2xl font-rajdhani font-bold text-xs sm:text-base transition-all ${
              currentPage === totalPages
                ? 'bg-gradient-to-b from-slate-800/60 via-slate-900/60 to-slate-950/60 text-gray-600 cursor-not-allowed border-t-2 border-l-2 border-slate-900/70 border-r-2 border-b-2 border-r-slate-950/80 border-b-slate-950/80'
                : 'bg-gradient-to-b from-slate-700/60 via-slate-800/60 to-slate-900/60 hover:from-slate-600/70 hover:via-slate-700/70 hover:to-slate-800/70 text-system-blue border-t-2 border-l-2 border-slate-500/50 border-r-2 border-b-2 border-r-slate-800/70 border-b-slate-900/70 hover:scale-105'
            }`}
            style={currentPage !== totalPages ? {
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4), 0 1px 4px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
            } : {
              boxShadow: 'inset 0 2px 6px rgba(0, 0, 0, 0.6)'
            }}
          >
            <span className="hidden sm:inline">Next →</span>
            <span className="sm:hidden">→</span>
          </button>
        </div>
      )}

      {/* Clear Confirmation Modal */}
      {showClearModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm animate-fade-in p-3 sm:p-4">
          <div 
            className="bg-gradient-to-b from-slate-900/95 via-black to-slate-900/95 backdrop-blur-xl rounded-3xl p-4 sm:p-8 max-w-md w-full animate-scale-in border-t-2 border-l-2 border-red-500/60 border-r-2 border-b-2 border-r-red-700/80 border-b-red-800/80"
            style={{
              boxShadow: '0 12px 48px rgba(239, 68, 68, 0.6), 0 4px 16px rgba(239, 68, 68, 0.4), inset 0 2px 4px rgba(255, 100, 100, 0.1), inset 0 -4px 12px rgba(0, 0, 0, 0.6)'
            }}
          >
            {/* Warning Icon */}
            <div className="text-center mb-4 sm:mb-6">
              <div className="text-4xl sm:text-6xl mb-3 sm:mb-4 animate-pulse">⚠️</div>
              <h2 className="text-xl sm:text-2xl font-bold font-orbitron text-red-400 mb-1 sm:mb-2">
                ［ WARNING ］
              </h2>
              <p className="text-gray-300 font-rajdhani text-base sm:text-lg">
                Clear Quest History?
              </p>
            </div>

            {/* Warning Message */}
            <div className="bg-red-900/30 border-2 border-red-500/50 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
              <p className="text-gray-300 font-rajdhani text-xs sm:text-sm leading-relaxed">
                This will clear your quest completion history.
              </p>
              <p className="text-gray-400 font-rajdhani text-[10px] sm:text-xs mt-1.5 sm:mt-2">
                ✓ Your <span className="text-green-400">lifetime statistics</span>, XP, and currency will NOT be affected.
              </p>
              <p className="text-gray-400 font-rajdhani text-[10px] sm:text-xs">
                ✓ <span className="text-blue-400">Daily quests</span> will be reset and available again tomorrow.
              </p>
              <p className="text-gray-400 font-rajdhani text-[10px] sm:text-xs">
                ✓ <span className="text-red-400">Regular completed quests</span> will be permanently deleted.
              </p>
              <p className="text-gray-400 font-rajdhani text-[10px] sm:text-xs mt-1">
                ✗ This action CANNOT be undone.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 sm:gap-3">
              <button
                onClick={() => setShowClearModal(false)}
                disabled={isClearing}
                className="flex-1 bg-gradient-to-b from-slate-700/60 via-slate-800/60 to-slate-900/60 hover:from-slate-600/70 hover:via-slate-700/70 hover:to-slate-800/70 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-2xl font-rajdhani font-bold text-xs sm:text-base transition-all disabled:opacity-50 border-t-2 border-l-2 border-slate-500/50 border-r-2 border-b-2 border-r-slate-800/70 border-b-slate-900/70 hover:scale-105 active:scale-95"
                style={{
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4), 0 1px 4px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                }}
              >
                CANCEL
              </button>
              <button
                onClick={handleClearCompleted}
                disabled={isClearing}
                className="flex-1 bg-gradient-to-b from-red-700/60 via-red-800/60 to-red-900/60 hover:from-red-600/70 hover:via-red-700/70 hover:to-red-800/70 text-red-400 hover:text-red-300 px-4 sm:px-6 py-2 sm:py-3 rounded-2xl font-rajdhani font-bold text-xs sm:text-base transition-all disabled:opacity-50 flex items-center justify-center gap-1.5 sm:gap-2 border-t-2 border-l-2 border-red-500/50 border-r-2 border-b-2 border-r-red-800/70 border-b-red-900/70 hover:scale-105 active:scale-95"
                style={{
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4), 0 1px 4px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 100, 100, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                }}
              >
                {isClearing ? (
                  <>
                    <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin"></div>
                    <span>CLEARING...</span>
                  </>
                ) : (
                  <>
                    <span>🗑️</span>
                    <span>CLEAR ALL</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistoryPage;

